import type { PurchaseRequisition, PurchaseSpendClass } from '../types/purchasing';
import { PURCHASE_SPEND_CLASS_LABELS, resolveSpendClass } from './purchasingSpendClass';

export type PurchaseStatHoverKind = 'issued' | 'purchased';

export type PurchaseStatHoverClassRow = {
  classId: PurchaseSpendClass;
  label: string;
  amount: number;
};

export type PurchaseStatHoverMonth = {
  key: string;
  label: string;
  amount: number;
};

export type PurchaseStatHoverSummary = {
  count: number;
  total: number;
  byClass: PurchaseStatHoverClassRow[];
  months: PurchaseStatHoverMonth[];
};

function matchesKind(row: PurchaseRequisition, kind: PurchaseStatHoverKind): boolean {
  if (kind === 'issued') return row.status === 'approved' && !row.hold;
  return row.status === 'purchased';
}

function rowAmount(row: PurchaseRequisition, kind: PurchaseStatHoverKind): number {
  if (kind === 'purchased' && row.purchasedAmount != null) return row.purchasedAmount;
  return row.amount;
}

function rowDate(row: PurchaseRequisition, kind: PurchaseStatHoverKind): Date {
  if (kind === 'purchased') return row.purchasedAt || row.approvedAt || row.createdAt;
  return row.approvedAt || row.createdAt;
}

export function purchaseMonthKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'short' });
}

function lastSixMonthKeys(now: Date): string[] {
  const keys: string[] = [];
  for (let i = 5; i >= 0; i -= 1) {
    keys.push(purchaseMonthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  }
  return keys;
}

/** Dollar totals and last-6-month buckets for the Purchasing toolbar chips. */
export function summarizePurchaseStatHover(
  rows: PurchaseRequisition[],
  kind: PurchaseStatHoverKind,
  now: Date = new Date(),
): PurchaseStatHoverSummary {
  const matched = rows.filter((row) => matchesKind(row, kind));
  const classTotals: Record<PurchaseSpendClass, number> = { job: 0, overhead: 0, capex: 0 };
  let total = 0;
  const monthSums = new Map<string, number>();

  for (const row of matched) {
    const raw = rowAmount(row, kind);
    const amount = Number.isFinite(raw) ? raw : 0;
    total += amount;
    classTotals[resolveSpendClass(row.costObject)] += amount;
    const key = purchaseMonthKey(rowDate(row, kind));
    monthSums.set(key, (monthSums.get(key) || 0) + amount);
  }

  const byClass = (['job', 'overhead', 'capex'] as const)
    .filter((id) => classTotals[id] > 0)
    .map((id) => ({
      classId: id,
      label: PURCHASE_SPEND_CLASS_LABELS[id],
      amount: classTotals[id],
    }));

  const months = lastSixMonthKeys(now).map((key) => ({
    key,
    label: monthLabel(key),
    amount: monthSums.get(key) || 0,
  }));

  return { count: matched.length, total, byClass, months };
}
